import Video from '../components/Video';
import { initCarousel } from '../helpers/carousel';

const selectors = {
  sizeGuideTab: '[data-size-guide-tab]',
  sizeGuideTabGroup: '[data-size-guide-tab-group]',
  sizeGuideContent: '[data-size-guide-content]',
  sizeGuideSecondaryTab: '[data-size-guide-secondary-tab]',
  sizeGuideSecondaryTabGroup: '[data-size-guide-secondary-tab-group]',
  carousel: '[data-splide-carousel]',
  splideList: '.splide__list',
  sizeToggle: '[data-size-toggle]',
  sizeTable: '[data-size-table]',
  progressBar: '[data-progress-bar]',
  hatType: '[data-hat-type]',
  video: '[data-video]',
};

export default class DynamicSizeGuide {
  constructor(section) {
    this.el = section.el;
    const sizeGuideTabGroups = this.el.querySelectorAll(
      selectors.sizeGuideTabGroup,
    );

    this.initTabs(sizeGuideTabGroups);
    this.activatePrimaryTab(sizeGuideTabGroups[0]);
    this.initVideos();
  }

  initVideos() {
    const videos = this.el.querySelectorAll(selectors.video);
    videos.forEach(video => {
      new Video(video);
    });
  }

  initTabs(groups) {
    groups.forEach(group => {
      const tab = group.querySelector(selectors.sizeGuideTab);
      tab.addEventListener('click', () => {
        this.deactivateAllTabs();
        this.activatePrimaryTab(group);
      });
    });
  }

  initializeCarousel(group) {
    // grab all secondary tabs
    const secondaryTabs = group.querySelectorAll(
      selectors.sizeGuideSecondaryTab,
    );
    // move them into the carousel
    secondaryTabs.forEach(tab => {
      const splideList = group.querySelector(selectors.splideList);
      tab.classList.add('splide__slide');
      splideList.appendChild(tab);
    });

    this.carousel = initCarousel(
      group,
      {
        perPage: 3,
        pagination: false,
        gap: '0.5rem',
        drag: false,
      },
      splide => this._updateProgressBar(splide),
      true,
    );

    // Updates the bar width whenever the carousel moves:
    this.carousel.on('move', () => this._updateProgressBar(this.carousel));
  }

  _updateProgressBar(splide) {
    const bar = splide.root.querySelector(selectors.progressBar);
    const end = splide.Components.Controller.getEnd() + 1;
    const rate = Math.min((splide.index + 1) / end, 1);
    bar.style.width = String(100 * rate) + '%';
  }

  activatePrimaryTab(group) {
    const secondaryTabs = group.querySelectorAll(
      selectors.sizeGuideSecondaryTab,
    );
    // get title of first tab
    const title = secondaryTabs[0].dataset.sizeGuideSecondaryTab;
    group.classList.add('active');

    this.initializeCarousel(group);
    this.initSecondaryTabs(group);
    this.activateSecondaryTab(group, title);
  }

  initSecondaryTabs(group) {
    const tabs = group.querySelectorAll(selectors.sizeGuideSecondaryTab);

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const title = tab.dataset.sizeGuideSecondaryTab;
        this.deactivateAllSecondaryTabs(group);
        this.activateSecondaryTab(group, title);
      });
    });
  }

  activateSecondaryTab(group, title) {
    const content = group.querySelector(
      `[data-size-guide-secondary-content="${title}"]`,
    );
    const tab = group.querySelector(
      `[data-size-guide-secondary-tab="${title}"]`,
    );
    const tabGroup = content.closest(selectors.sizeGuideSecondaryTabGroup);
    tabGroup.classList.add('active');
    tab.classList.add('active');
    this._initSizeToggle(group);
    this._updateHatType(group, title);
  }

  _updateHatType(group, title) {
    const hatType = group.querySelector(selectors.hatType);
    hatType.textContent = title;
  }

  deactivateAllTabs() {
    const sizeGuideTabGroups = this.el.querySelectorAll(
      selectors.sizeGuideTabGroup,
    );

    if (this.carousel) {
      this.carousel.destroy();
    }
    sizeGuideTabGroups.forEach(group => {
      group.classList.remove('active');
      this.deactivateAllSecondaryTabs(group);
    });
  }

  deactivateAllSecondaryTabs(group) {
    const tabs = group.querySelectorAll(selectors.sizeGuideSecondaryTab);
    const secondaryTabGroups = group.querySelectorAll(
      selectors.sizeGuideSecondaryTabGroup,
    );
    this._cleanupSizeToggle(group);

    secondaryTabGroups.forEach(g => {
      g.classList.remove('active');
    });

    tabs.forEach(tab => {
      tab.classList.remove('active');
    });
  }

  _initSizeToggle = group => {
    const sizeToggles = group.querySelectorAll(selectors.sizeToggle);

    sizeToggles.forEach(toggle => {
      toggle.addEventListener('click', () =>
        this._handleSizeToggleClick(toggle),
      );
    });
  };

  _handleSizeToggleClick = toggle => {
    const sizeTables = this.el.querySelectorAll(selectors.sizeTable);
    const sizeToggles = this.el.querySelectorAll(selectors.sizeToggle);

    const toggleType = toggle.dataset.sizeToggle;

    sizeToggles.forEach(t => {
      if (t.dataset.sizeToggle === toggleType) {
        t.classList.add('active');
      } else {
        t.classList.remove('active');
      }
    });
    sizeTables.forEach(t => {
      if (t.dataset.sizeTable === toggleType) {
        t.classList.remove('hidden');
        t.classList.add('grid');
      } else {
        t.classList.add('hidden');
        t.classList.remove('grid');
      }
    });
  };

  _cleanupSizeToggle = group => {
    const sizeToggles = group.querySelectorAll(selectors.sizeToggle);

    sizeToggles.forEach(toggle => {
      toggle.removeEventListener('click', () =>
        this._handleSizeToggleClick(toggle),
      );
    });
  };
}
